import { useState, useEffect } from "react";
import { rtdb } from "../../services/firebase";
import { UserChannel, USER_CHANNEL_LAST_UPDATE } from "../../types/db/chat";
import { UserChannelService } from "../../types/services/chat";
import { WithID } from "../../types/db";

/** Channel info is the info of a user channel with the channel id */
type UserChannelInfo = WithID<UserChannel>;

/** The default limit of fetching data */
const DEFAULT_LIMIT_TO_LAST = 5;

/**
 * Hook which takes care of fetching and updating the list of threads of a user
 * @param userId The user ID
 * @returns the array of threads in the inbox
 */
export const useUserChannels = (userId: string): {
    /** The list of threads in the inbox */
    channels: UserChannelInfo[];
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
} => {
    const [isLoading, setIsLoading] = useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, _setError] = useState('');
    const [channels, setChannels] = useState<UserChannelInfo[]>([]);

    useEffect(() => {
        if (!userId) {
            setIsLoading(false);
            //in the case of user not defined->no channels
            setChannels([]);
            return;
        }

        //update the current listing with the listing info from BE
        setIsLoading(true)
        //subscribe to any added child of that user's channel
        const fetchUserChannel = (new UserChannelService(rtdb)).getParentRef(userId);

        fetchUserChannel.orderByChild(USER_CHANNEL_LAST_UPDATE).limitToFirst(DEFAULT_LIMIT_TO_LAST)
            .on("child_added", (snapshot) => {
                const channelID = snapshot.key!;
                const userChannelInfo: UserChannel | null = snapshot.val();
                // console.log('child_added', channelID, userChannelInfo)
                setIsLoading(false)
                if (!userChannelInfo) {
                    //can happen if the chat is empty
                    return;
                }

                // append the item on the list
                setChannels(currentItems => [
                    {
                        id: channelID,
                        ...userChannelInfo,
                    },
                    ...currentItems,
                ]);
            });

        fetchUserChannel.on("child_changed", (snapshot) => {
            const userChannelInfo: UserChannel = snapshot.val();
            const channelID = snapshot.key!;
            //console.log('child_changed', channelID, userChannelInfo)
            // modify the item on the list
            setChannels(currentItems => {
                const index = currentItems.findIndex(item => item.id === channelID);
                if (index === -1) {
                    //if not found in the list, return as is
                    return currentItems;
                }

                return [
                    ...currentItems.slice(0, index),
                    {
                        id: channelID,
                        ...userChannelInfo,
                    },
                    ...currentItems.slice(index + 1),
                ]
            });
        })

        fetchUserChannel.on("child_removed", (snapshot) => {
            // const userChannelInfo: UserChannel = snapshot.val();
            const channelID = snapshot.key!;
            // console.log('child_removed', channelID, userChannelInfo)

            //remove the channel from the channel list
            setChannels(currentItems =>
                currentItems.filter(item => item.id !== channelID)
            );
        })

        return () => {
            //unsubscribe to all listener for the user channel
            fetchUserChannel.off();
            //reset the user channel list (user changed)
            setChannels([]);
        };
    }, [userId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the user doesn't have channels
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);

    return {
        channels,
        isLoading,
        error,
    }
}